// A tab older than the last deploy asks for chunks that no longer exist: Pages answers 404, or 200
// with index.html (its SPA fallback), and the dynamic import rejects. Nothing catches that, so React
// unmounts the whole tree and the page is blank until the user reloads by hand. One reload fetches
// the new index.html and its chunks; it is allowed once per build, so a chunk that is truly
// missing ends in the error instead of a reload loop.

const STORAGE_KEY = "proef:chunk-reload";

const CHUNK_ERROR =
  /dynamically imported module|importing a module script failed|error loading dynamically imported|unable to preload css|failed to load module script|is not a valid javascript mime type/i;

/** The browsers' and Vite's wording for a lazy chunk that could not load. */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.name === "ChunkLoadError" || CHUNK_ERROR.test(error.message);
}

export interface ChunkRecoveryEnv {
  readonly build: string;
  readonly storage: Pick<Storage, "getItem" | "setItem"> | undefined;
  readonly reload: () => void;
}

const defaultEnv = (): ChunkRecoveryEnv => {
  let storage: Storage | undefined;
  try {
    ({ sessionStorage: storage } = window);
  } catch {
    storage = undefined;
  }
  return { build: import.meta.url, storage, reload: () => window.location.reload() };
};

let reloading = false;

/**
 * Reloads once for a chunk-load error and returns true when a reload is under way, false when the
 * error is something else or this build already tried. The build is the entry module's URL, which
 * changes with every deploy, so the guard needs no clock.
 */
export function recoverFromChunkError(
  error: unknown,
  env: ChunkRecoveryEnv = defaultEnv(),
): boolean {
  if (reloading) return true;
  if (!isChunkLoadError(error)) return false;
  const { storage } = env;
  if (storage === undefined) return false;
  try {
    if (storage.getItem(STORAGE_KEY) === env.build) return false;
    storage.setItem(STORAGE_KEY, env.build);
  } catch {
    // Storage blocked: without a guard a reload could loop, so do not reload.
    return false;
  }
  reloading = true;
  env.reload();
  return true;
}

/** Test seam: forget that this page already started a reload. */
export function resetChunkRecovery(): void {
  reloading = false;
}

/** Vite fires this when a dynamic import's preloaded dependency fails to load. */
export function startChunkRecovery(): void {
  window.addEventListener("vite:preloadError", (event) => {
    const { payload }: { payload?: unknown } = event as Event & { payload?: unknown };
    recoverFromChunkError(payload);
  });
}
